import React, { useCallback, useEffect, useState } from "react";
import CategoryTree from "./CategoryTree";
import CategoryEditor from "./CategoryEditor";
import { Button } from "react-bootstrap";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import { toast } from "react-toastify";

export default function CategoriesLayout({
  categories,
  addRootCategory,
  addSubCategory,
  updateCategory,
  deleteCategory,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showEditor, setShowEditor] = useState(false); // for mobile
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editorMode, setEditorMode] = useState("default");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectedCategory = selectedId
    ? findCategory(categories, selectedId)
    : null;

  function findCategory(list, id) {
    for (const item of list) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = findCategory(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  const handleSelect = (id) => {
    setSelectedId(id);

    const cat = findCategory(categories, id);

    // if currently creating AND selected node is LEAF → stop create mode
    if (editorMode === "create" && cat?.type === "LEAF") {
      setEditorMode("view");
      return;
    }
    // setEditorMode("view");

    setEditorMode((prev) =>
      prev === "edit" || prev === "create" ? prev : "view"
    );

    if (isMobile) setShowEditor(true);
  };

  const handleCreate = useCallback((parentId) => {
    setSelectedId(parentId);
    setEditorMode("create");
    if (isMobile) setShowEditor(true);
  }, []);

  const handleRename = useCallback((id) => {
    setSelectedId(id);
    setEditorMode("edit");
    if (isMobile) setShowEditor(true);
  }, []);

  const handleDelete = useCallback((id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  }, []);

  const handleBack = () => {
    setShowEditor(false);
    setSelectedId(null);
  };

  const onSaveCategory = async (saveMode, payload) => {
    if (saveMode === "create") {
      if (!payload.name || !payload.type) {
        return toast.error("Name and type fields required");
      }

      setSubmitting(true);
      try {
        const newCategory = await addSubCategory(payload);
        setSelectedId(newCategory.id);
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
        setEditorMode("view");
      }
    }

    if (saveMode === "createRoot") {
      if (!payload.name) {
        return toast.error("Name field is required");
      }

      setSubmitting(true);
      try {
        const newCategory = await addRootCategory(payload);
        setSelectedId(newCategory.id);
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
        setEditorMode("view");
      }
    }

    if (saveMode === "edit") {
      if (!payload.name) {
        return toast.error("Name field is required");
      }

      setSubmitting(true);
      try {
        const updatedCategory = await updateCategory(selectedId, payload);
        setSelectedId(updatedCategory.id);
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
        setEditorMode("view");
      }
    }
  };

  const onConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await deleteCategory(selectedId);
      setEditorMode("default");
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        display: isMobile ? "block" : "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        border: "1px solid #eee",
      }}
    >
      {/* on mobile: only show tree when showEditor = false */}
      {/* on desktop: always show tree */}
      {(!isMobile || !showEditor) && (
        <div style={{ borderRight: !isMobile ? "1px solid #eee" : "none" }}>
          <CategoryTree
            data={categories}
            onSelect={handleSelect}
            onCreate={handleCreate}
            onRename={handleRename}
            onDelete={handleDelete}
            selectedId={selectedId}
          />
        </div>
      )}

      {/* on mobile: only show tree when showEditor = true */}
      {/* on desktop: always show editor */}
      {(!isMobile || showEditor) && (
        <div className="p-3 px-4 px-lg-3 mt-lg-0">
          {isMobile && (
            <Button
              variant="light"
              onClick={handleBack}
              className="text-xs mb-3"
            >
              ← Back
            </Button>
          )}

          <CategoryEditor
            category={selectedCategory}
            parentCategory={editorMode === "create" ? selectedCategory : null}
            mode={editorMode}
            submitting={submitting}
            onSave={(form) => {
              if (editorMode === "edit") {
                onSaveCategory("edit", form);
              }

              if (editorMode === "createRoot") {
                onSaveCategory("createRoot", form);
              }

              if (editorMode === "create") {
                onSaveCategory("create", {
                  parentId: selectedId,
                  ...form,
                });
              }

              if (form === "edit") {
                setEditorMode("edit");
              }

              if (form === "createRoot") {
                setEditorMode("createRoot");
              }
            }}
            onCancel={() =>
              editorMode === "view" || editorMode === "createRoot"
                ? setEditorMode("default")
                : setEditorMode("view")
            }
          />

          <ConfirmDeleteModal
            itemName="category"
            show={showDeleteModal}
            setShow={setShowDeleteModal}
            onConfirm={onConfirmDelete}
            loading={submitting}
          />
        </div>
      )}
    </div>
  );
}
